import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SERVER_URL } from '../../../constants';
import { Eye, Star } from 'lucide-react';

const TopSellingProducts = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 11, minutes: 59, seconds: 8 });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const countdown = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        clearInterval(countdown);
        return { hours: 0, minutes: 0, seconds: 0 };
      });
    }, 1000);
    return () => clearInterval(countdown);
  }, []);

  useEffect(() => {
    const fetchTopDeals = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}/api/v1/product`);
        const { data } = response.data;
        const topDeals = data.filter((product) => product.top === 'topSelling');
        setProducts(topDeals);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        setLoading(false);
      }
    };
    fetchTopDeals();
  }, []);

  const handleViewProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  const renderStars = (rating) => {
    const stars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    return (
      <div className="flex items-center text-yellow-500">
        {[...Array(stars)].map((_, i) => (
          <Star key={i} size={16} fill="currentColor" strokeWidth={0} />
        ))}
        {halfStar && <Star size={16} fill="currentColor" strokeWidth={0} className="opacity-60" />}
        <span className="ml-2 text-xs font-medium text-gray-600">({rating || '4.5'})</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-lg font-semibold text-gray-700 animate-pulse">
          Loading Top Sellers...
        </div>
      </div>
    );
  }

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-10 flex flex-col sm:flex-row justify-between items-center animate-fade-in">
          <div className="text-center sm:text-left mb-6 sm:mb-0">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2 tracking-tight">
              Top <span className="text-blue-600">Selling Picks</span>
            </h2>
            <p className="text-md sm:text-lg text-gray-600 max-w-xl">
              Discover the most popular products loved by customers like you!
            </p>
            <div className="mt-4 flex justify-center sm:justify-start gap-3 text-lg font-semibold text-gray-800">
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-md">{timeLeft.hours.toString().padStart(2, '0')}</span>
              <span className="text-gray-500">:</span>
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-md">{timeLeft.minutes.toString().padStart(2, '0')}</span>
              <span className="text-gray-500">:</span>
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-md">{timeLeft.seconds.toString().padStart(2, '0')}</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/categories')}
            className="text-blue-600 font-semibold text-lg hover:underline transition-all duration-300"
          >
            Explore More
          </button>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, index) => (
            <div
              key={product._id}
              className="group bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image Section */}
              <div className="relative overflow-hidden">
                <img
                  src={product.imgUrl[0] || '/api/placeholder/300/300'}
                  alt={product.name}
                  className="h-56 w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                />
                <button
                  onClick={() => handleViewProduct(product._id)}
                  className="absolute top-2 right-2 bg-white p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-gray-100"
                >
                  <Eye size={18} className="text-gray-600" />
                </button>
                {product.discount && (
                  <span className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
                    {product.discount}% OFF
                  </span>
                )}
              </div>

              {/* Content Section */}
              <div className="p-4">
                <h3 className="text-md font-medium text-gray-900 line-clamp-2 min-h-[2.5rem] group-hover:text-blue-600 transition-colors duration-300">
                  {product.name}
                </h3>
                <div className="mt-2">{renderStars(product.rating || 4.5)}</div>

                {/* Pricing */}
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-lg font-bold text-gray-900">
                    ₹{product.price.toFixed(2)}
                  </span>
                  {product.originalPrice && (
                    <span className="text-sm text-gray-500 line-through">
                      ₹{product.originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>

                {/* View Details Button */}
                <button
                  onClick={() => handleViewProduct(product._id)}
                  className="mt-4 w-full bg-blue-600 text-white py-2 rounded-md text-sm font-semibold hover:bg-blue-700 transition-all duration-300"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Show More Button */}
      
      </div>
    </section>
  );
};

export default TopSellingProducts;
